import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getTrialsIcon, getWithTrainerIcon } from "../MemberReportMocks.js";
import CalendarLoader from "../../../common-components/CalendarLoader.jsx";

const GraphicalReport = () => {
  const {
    gymDistributionStats,
    gymDistributionStatsLoading,
    gymDistributionStatsError,
  } = useSelector((state) => state.prospects);

  const [statsData, setStatsData] = useState(null);

  const trainerBookedPercentage = statsData?.trainer_analysis
    ? (statsData.trainer_analysis.booked_slots /
        statsData.trainer_analysis.total_slots) *
      100
    : 0;

  const checkinAssignedPercentage = statsData?.checkin_analysis
    ? (statsData.checkin_analysis.with_trainer /
        statsData.checkin_analysis.total_checkins) *
      100
    : 0;

  useEffect(() => {
    if (
      !gymDistributionStatsLoading &&
      !gymDistributionStatsError &&
      gymDistributionStats?.status
    ) {
      setStatsData(gymDistributionStats?.data);
    } else {
      setStatsData(null);
    }
  }, [
    gymDistributionStatsLoading,
    gymDistributionStatsError,
    gymDistributionStats,
  ]);

  return (
    <section className="mr-analytics-section" aria-label="Gym analytics">
      <div className="mr-analytics-grid">
      {gymDistributionStatsLoading ? (
        <CalendarLoader daysCount={3} shiftsPerDay={3} />
      ) :   <>
        <article className="mr-card" aria-labelledby="trainer-analysis-title">
          <header className="mr-card__header">
            <div className="mr-card__title">
              {getWithTrainerIcon("mr-icon")}

              <h3 id="trainer-analysis-title" className="mr-card__heading">
                Trainer Analysis
              </h3>
            </div>
          </header>

          <div className="mr-card__body mr-layout--split">
            <div
              className="mr-ta-ring"
              style={{
                "--size": "144px",
                "--thickness": "12",
                "--r": "66",
                "--value": trainerBookedPercentage,
                "--progress": "#22c55e",
              }}
            >
              <svg
                className="mr-ta-ring__svg"
                viewBox="0 0 144 144"
                width="144"
                height="144"
                aria-hidden="true"
              >
                <circle
                  className="mr-ta-ring__track"
                  cx="72"
                  cy="72"
                  r="66"
                ></circle>
                <circle
                  className="mr-ta-ring__progress"
                  cx="72"
                  cy="72"
                  r="66"
                ></circle>
              </svg>
              <div className="mr-ta-ring__center" aria-live="polite">
                <div className="mr-ta-ring__value">
                  {statsData?.trainer_analysis?.booked_slots || 0}
                </div>
                <div className="mr-ta-ring__label">Booked</div>
              </div>
            </div>

            <dl className="mr-kv">
              <div className="mr-kv__row">
                <dt>Available Trainers</dt>
                <dd>{statsData?.trainer_analysis?.available_trainers || 0}</dd>
              </div>
              <div className="mr-kv__row">
                <dt>Total Slots</dt>
                <dd>{statsData?.trainer_analysis?.total_slots || 0}</dd>
              </div>
              <div className="mr-kv__row">
                <dt>Booked Slots</dt>
                <dd className="mr-text--success">
                  {statsData?.trainer_analysis?.booked_slots || 0}
                </dd>
              </div>
            </dl>
          </div>
        </article>

        <article className="mr-card" aria-labelledby="trial-distribution-title">
          <header className="mr-card__header">
            <div className="mr-card__title">
              {getTrialsIcon("mr-icon")}

              <h3 id="trial-distribution-title" className="mr-card__heading">
                Trial Distribution
              </h3>
            </div>

            {/* <span className="mr-badge">
              {getWaveIcon()}
              {trialDistribution.todayTrend} today
            </span> */}
          </header>
          <div className="mr-card__body">
            {[
              {
                label: "Day 1",
                value: statsData?.trial_distribution?.day1 || 0,
              },
              {
                label: "Day 2",
                value: statsData?.trial_distribution?.day2 || 0,
              },
              {
                label: "Day 3",
                value: statsData?.trial_distribution?.day3 || 0,
              },
            ].map((day, index) => {
              const totalTrials =
                statsData?.trial_distribution?.total_active_trials || 1;
              const percentage =
                totalTrials > 0 ? (day.value / totalTrials) * 100 : 0;

              return (
                <div key={index} className="mr-bar-row">
                  <div className="mr-bar-row__meta">
                    <span className="mr-label">{day.label}</span>
                    <span className="mr-value">{day.value}</span>
                  </div>
                  <div className="mr-bar-track">
                    <div
                      className={`mr-bar-fill ${
                        index === 0
                          ? "mr-bar--info"
                          : index === 1
                          ? "mr-bar--warning"
                          : "mr-bar--success"
                      }`}
                      style={{ "--w": `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
            <div className="mr-divider" role="separator" aria-hidden="true" />
            <div className="mr-total-row">
              <span className="mr-label">Total Active Trials</span>
              <span className="mr-total">
                {statsData?.trial_distribution?.total_active_trials || 0}
              </span>
            </div>
          </div>
        </article>

        <article className="mr-card" aria-labelledby="checkin-analysis-title">
          <header className="mr-card__header">
            <div className="mr-card__title">
              {getTrialsIcon("mr-icon")}

              <h3 id="checkin-analysis-title" className="mr-card__heading">
                Check-in Analysis
              </h3>
            </div>
          </header>
          <div className="mr-card__body mr-layout--split">
            <div
              className="mr-ta-ring"
              style={{
                "--size": "144px",
                "--thickness": "12",
                "--r": "66",
                "--value": checkinAssignedPercentage,
                "--progress": "#1e66f5",
              }}
            >
              <svg
                className="mr-ta-ring__svg"
                viewBox="0 0 144 144"
                width="144"
                height="144"
                aria-hidden="true"
              >
                <circle
                  className="mr-ta-ring__track"
                  cx="72"
                  cy="72"
                  r="66"
                ></circle>
                <circle
                  className="mr-ta-ring__progress"
                  cx="72"
                  cy="72"
                  r="66"
                ></circle>
              </svg>
              <div className="mr-ta-ring__center" aria-live="polite">
                <div className="mr-ta-ring__value">
                  {statsData?.checkin_analysis?.with_trainer || 0}
                </div>
                <div className="mr-ta-ring__label">Assigned</div>
              </div>
            </div>
            <dl className="mr-kv">
              <div className="mr-kv__row">
                <dt>Total Check-ins</dt>
                <dd>{statsData?.checkin_analysis?.total_checkins || 0}</dd>
              </div>
              <div className="mr-kv__row">
                <dt>With Trainer</dt>
                <dd className="mr-text--success">
                  {statsData?.checkin_analysis?.with_trainer || 0}
                </dd>
              </div>
              <div className="mr-kv__row">
                <dt>Without Trainer</dt>
                <dd className="mr-text--danger">
                  {statsData?.checkin_analysis?.without_trainer || 0}
                </dd>
              </div>
            </dl>
          </div>
        </article>
        </>}
      </div>
    </section>
  );
};

export default GraphicalReport;
